"use client";

import { ListFilter, SlidersHorizontal, X } from "lucide-react";
import { useEffect, useId, useState } from "react";
import {
  AccordionContent,
  AccordionItem,
  AccordionPrimitive,
  AccordionTrigger,
} from "../../elements/accordion/accordion";
import { Button } from "../../elements/button/button";
import { ColorSwatch } from "../../elements/color-swatch/color-swatch";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "../../elements/dialog/dialog";
import { RadioGroup } from "../../elements/radio-group/radio-group";
import { TextD2C } from "../../elements/text-d2c/text";
import { cn } from "../../lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../patterns/popover/popover";

export type ProductFilterOptionD2C = {
  id: string;
  label: string;
  count?: number;
  selected: boolean;
  query: string;
  disabled?: boolean;
  colors?: string[];
  isFallbackColor?: boolean;
};

export type ProductOptionFilterGroupD2C = {
  type: "options";
  code: string;
  title: string;
  selection: "single" | "multiple";
  display?: "list" | "swatches";
  options: ProductFilterOptionD2C[];
  defaultOpen?: boolean;
};

export type ProductRangeFilterGroupD2C = {
  type: "range";
  code: string;
  title: string;
  min: number;
  max: number;
  selectedMin: number;
  selectedMax: number;
  step?: number;
  defaultOpen?: boolean;
};

export type ProductFilterGroupD2C =
  | ProductOptionFilterGroupD2C
  | ProductRangeFilterGroupD2C;

export type ActiveProductFilterD2C = {
  id: string;
  groupCode: string;
  label: string;
  query: string;
};

export type ProductSortOptionD2C = {
  code: string;
  label: string;
  selected: boolean;
};

export type ProductListingControlsMicrocopies = {
  applyFilters: string;
  filterProducts: string;
  filterTitle: string;
  closeFilters: string;
  closeSort: string;
  sortProducts: string;
  sortTitle: string;
  clearAllFilters: string;
  removeFilter: string;
  noFilterOptionsAvailable: string;
  rangeMinimum: string;
  rangeMaximum: string;
};

type ProductListingActiveFiltersD2CProps = {
  activeFilters: ActiveProductFilterD2C[];
  disabled?: boolean;
  microcopies: ProductListingControlsMicrocopies;
  onFilterChange?: (query: string) => void;
};

export function ProductListingActiveFiltersD2C(
  props: Readonly<ProductListingActiveFiltersD2CProps>
) {
  if (props.activeFilters.length === 0) {
    return null;
  }

  return (
    <ul
      aria-label={props.microcopies.filterProducts}
      className="mb-8 flex flex-wrap gap-2 lg:mb-4"
      data-testid="product-listing-active-filters"
    >
      {props.activeFilters.map((filter) => (
        <li key={filter.id}>
          <Button
            aria-label={`${props.microcopies.removeFilter}: ${filter.label}`}
            className="h-7 gap-1 rounded-full bg-alabastergrey px-3 py-1 font-brand font-normal text-black text-sm leading-5 shadow-none hover:bg-alabastergrey/80 focus-visible:ring-black/30"
            disabled={props.disabled}
            onClick={() => props.onFilterChange?.(filter.query)}
            size="sm"
            type="button"
            variant="secondary"
          >
            <X aria-hidden="true" className="size-3" />
            {filter.label}
          </Button>
        </li>
      ))}
    </ul>
  );
}

type ProductListingToolbarProps = {
  disabled?: boolean;
  filtersOpen: boolean;
  mobileFiltersOpen: boolean;
  microcopies: ProductListingControlsMicrocopies;
  onMobileFiltersOpen: () => void;
  onFiltersToggle: () => void;
  onSortChange?: (code: string) => void;
  selectedSort?: string;
  showFilters?: boolean;
  showSort?: boolean;
  sortOptions: ProductSortOptionD2C[];
};

export function ProductListingToolbarD2C(
  props: Readonly<ProductListingToolbarProps>
) {
  const [sortOpen, setSortOpen] = useState(false);
  const [mobileSortOpen, setMobileSortOpen] = useState(false);
  const hasSortOptions = props.sortOptions.length > 0;
  const hasBothControls = props.showFilters && props.showSort;

  return (
    <div
      className={cn(
        "grid w-full gap-2 lg:flex lg:w-auto lg:flex-wrap lg:items-center",
        hasBothControls ? "grid-cols-2" : "grid-cols-1"
      )}
      data-testid="product-listing-actions"
    >
      {props.showFilters ? (
        <>
          <Button
            aria-expanded={props.filtersOpen}
            className="hidden lg:inline-flex"
            data-testid="product-listing-filter"
            disabled={props.disabled}
            mode="D2C"
            onClick={props.onFiltersToggle}
            size="default"
            variant="outline"
          >
            {props.filtersOpen ? (
              <X aria-hidden="true" className="size-4" />
            ) : (
              <SlidersHorizontal aria-hidden="true" className="size-4" />
            )}
            {props.filtersOpen
              ? props.microcopies.closeFilters
              : props.microcopies.filterProducts}
          </Button>
          <Button
            aria-expanded={props.mobileFiltersOpen}
            aria-haspopup="dialog"
            className="w-full lg:hidden"
            data-testid="product-listing-filter-mobile"
            disabled={props.disabled}
            mode="D2C"
            onClick={props.onMobileFiltersOpen}
            size="default"
            variant="outline"
          >
            <SlidersHorizontal aria-hidden="true" className="size-4" />
            {props.microcopies.filterProducts}
          </Button>
        </>
      ) : null}

      {props.showSort ? (
        hasSortOptions ? (
          <>
            <div className="hidden lg:block">
              <Popover onOpenChange={setSortOpen} open={sortOpen}>
                <PopoverTrigger asChild>
                  <Button
                    aria-expanded={sortOpen}
                    aria-haspopup="dialog"
                    data-testid="product-listing-sort"
                    disabled={props.disabled}
                    mode="D2C"
                    size="default"
                    variant="outline"
                  >
                    <ListFilter aria-hidden="true" className="size-4" />
                    {props.microcopies.sortProducts}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  className="w-auto min-w-52 p-5"
                  data-testid="product-listing-sort-menu"
                  sideOffset={10}
                >
                  <RadioGroup
                    labelVariant="body-sm"
                    name="product-listing-sort-options"
                    onValueChange={(code) => {
                      props.onSortChange?.(code);
                      setSortOpen(false);
                    }}
                    options={props.sortOptions.map((option) => ({
                      label: option.label,
                      value: option.code,
                    }))}
                    value={props.selectedSort}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <Button
              aria-expanded={mobileSortOpen}
              aria-haspopup="dialog"
              className="w-full lg:hidden"
              data-testid="product-listing-sort-mobile"
              disabled={props.disabled}
              mode="D2C"
              onClick={() => setMobileSortOpen(true)}
              size="default"
              variant="outline"
            >
              <ListFilter aria-hidden="true" className="size-4" />
              {props.microcopies.sortProducts}
            </Button>

            <Dialog onOpenChange={setMobileSortOpen} open={mobileSortOpen}>
              <DialogContent
                animation="slide-from-left"
                aria-describedby={undefined}
                className="flex flex-col lg:hidden"
                data-testid="product-listing-sort-mobile-menu"
                hideCloseButton
                overlayClassName="bg-black/50 lg:hidden"
                side="left"
              >
                <header className="flex shrink-0 items-center justify-between border-alabastergrey border-b px-3 pt-8 pb-4">
                  <DialogTitle className="font-bold font-brand text-xl leading-6">
                    {props.microcopies.sortTitle}
                  </DialogTitle>
                  <DialogClose asChild>
                    <Button
                      aria-label={props.microcopies.closeSort}
                      className="-mr-2"
                      size="icon"
                      variant="ghost"
                    >
                      <X aria-hidden="true" className="size-6" />
                    </Button>
                  </DialogClose>
                </header>
                <RadioGroup
                  className="gap-3 px-3 pt-8"
                  disabled={props.disabled}
                  labelVariant="body-sm"
                  name="product-listing-sort-options-mobile"
                  onValueChange={props.onSortChange}
                  options={props.sortOptions.map((option) => ({
                    label: option.label,
                    value: option.code,
                  }))}
                  value={props.selectedSort}
                />
              </DialogContent>
            </Dialog>
          </>
        ) : (
          <>
            <Button
              className="hidden lg:inline-flex"
              data-testid="product-listing-sort"
              disabled
              mode="D2C"
              size="default"
              variant="outline"
            >
              <ListFilter aria-hidden="true" className="size-4" />
              {props.microcopies.sortProducts}
            </Button>
            <Button
              className="w-full lg:hidden"
              data-testid="product-listing-sort-mobile"
              disabled
              mode="D2C"
              size="default"
              variant="outline"
            >
              <ListFilter aria-hidden="true" className="size-4" />
              {props.microcopies.sortProducts}
            </Button>
          </>
        )
      ) : null}
    </div>
  );
}

type ProductRangeFilterProps = {
  disabled?: boolean;
  group: ProductRangeFilterGroupD2C;
  microcopies: Pick<
    ProductListingControlsMicrocopies,
    "rangeMinimum" | "rangeMaximum"
  >;
  onChange?: (code: string, range: [number, number]) => void;
};

function ProductRangeFilterD2C(props: Readonly<ProductRangeFilterProps>) {
  const id = useId();
  const step = props.group.step ?? 1;

  function updateMinimum(value: number) {
    props.onChange?.(props.group.code, [
      Math.min(value, props.group.selectedMax),
      props.group.selectedMax,
    ]);
  }

  function updateMaximum(value: number) {
    props.onChange?.(props.group.code, [
      props.group.selectedMin,
      Math.max(value, props.group.selectedMin),
    ]);
  }

  return (
    <div className="space-y-4 py-2">
      <div className="grid grid-cols-2 gap-4">
        <label className="space-y-1 font-brand text-sm" htmlFor={`${id}-min`}>
          <span>{props.microcopies.rangeMinimum}</span>
          <input
            className="h-10 w-full border border-black bg-white px-3 text-base"
            disabled={props.disabled}
            id={`${id}-min`}
            max={props.group.max}
            min={props.group.min}
            onChange={(event) => updateMinimum(Number(event.target.value))}
            step={step}
            type="number"
            value={props.group.selectedMin}
          />
        </label>
        <label className="space-y-1 font-brand text-sm" htmlFor={`${id}-max`}>
          <span>{props.microcopies.rangeMaximum}</span>
          <input
            className="h-10 w-full border border-black bg-white px-3 text-base"
            disabled={props.disabled}
            id={`${id}-max`}
            max={props.group.max}
            min={props.group.min}
            onChange={(event) => updateMaximum(Number(event.target.value))}
            step={step}
            type="number"
            value={props.group.selectedMax}
          />
        </label>
      </div>
      <div className="grid gap-2">
        <input
          aria-label={props.microcopies.rangeMinimum}
          disabled={props.disabled}
          max={props.group.max}
          min={props.group.min}
          onChange={(event) => updateMinimum(Number(event.target.value))}
          step={step}
          type="range"
          value={props.group.selectedMin}
        />
        <input
          aria-label={props.microcopies.rangeMaximum}
          disabled={props.disabled}
          max={props.group.max}
          min={props.group.min}
          onChange={(event) => updateMaximum(Number(event.target.value))}
          step={step}
          type="range"
          value={props.group.selectedMax}
        />
      </div>
    </div>
  );
}

type ProductOptionFilterProps = {
  disabled?: boolean;
  group: ProductOptionFilterGroupD2C;
  onChange?: (query: string) => void;
};

function ProductOptionFilterD2C(props: Readonly<ProductOptionFilterProps>) {
  const name = useId();

  if (props.group.display === "swatches") {
    return (
      <fieldset className="flex flex-wrap gap-3 py-2">
        <legend className="sr-only">{props.group.title}</legend>
        {props.group.options.map((option) => {
          const count = option.count === undefined ? "" : ` (${option.count})`;

          return (
            <ColorSwatch
              className="m-0 h-6 w-10 border-alabastergrey"
              colors={option.colors ?? ["#808080"]}
              disabled={props.disabled || option.disabled}
              isFallbackColor={option.isFallbackColor}
              key={option.id}
              label={`${option.label}${count}`}
              onClick={() => props.onChange?.(option.query)}
              selected={option.selected}
            />
          );
        })}
      </fieldset>
    );
  }

  return (
    <fieldset className="flex flex-col gap-4 py-2">
      <legend className="sr-only">{props.group.title}</legend>
      {props.group.options.map((option) => {
        const id = `${name}-${option.id}`;
        const isDisabled = props.disabled || option.disabled;

        return (
          <label
            className={cn(
              "flex cursor-pointer items-center gap-3 font-brand text-sm leading-5",
              isDisabled && "cursor-not-allowed opacity-50"
            )}
            htmlFor={id}
            key={option.id}
          >
            <input
              checked={option.selected}
              className={cn(
                "size-4 shrink-0 border border-black bg-white accent-black",
                props.group.selection === "single" && "rounded-full"
              )}
              disabled={isDisabled}
              id={id}
              name={name}
              onChange={() => props.onChange?.(option.query)}
              type={props.group.selection === "single" ? "radio" : "checkbox"}
            />
            <span>
              {option.label}
              {option.count === undefined ? null : (
                <span className="ml-2 text-black/60">({option.count})</span>
              )}
            </span>
          </label>
        );
      })}
    </fieldset>
  );
}

export type ProductListingFiltersD2CProps = {
  activeFilters: ActiveProductFilterD2C[];
  className?: string;
  disabled?: boolean;
  filterGroups: ProductFilterGroupD2C[];
  microcopies: ProductListingControlsMicrocopies;
  onClearAll?: () => void;
  onFilterChange?: (query: string) => void;
  onRangeChange?: (code: string, range: [number, number]) => void;
  showResetButton?: boolean;
};

function hasSelectedFilter(group: ProductFilterGroupD2C): boolean {
  if (group.type === "range") {
    return group.selectedMin !== group.min || group.selectedMax !== group.max;
  }

  return group.options.some((option) => option.selected);
}

function ProductListingResetFiltersButtonD2C(
  props: Readonly<
    Pick<
      ProductListingFiltersD2CProps,
      "activeFilters" | "disabled" | "microcopies" | "onClearAll"
    > & { className?: string }
  >
) {
  return (
    <Button
      className={cn("w-full", props.className)}
      disabled={props.disabled || props.activeFilters.length === 0}
      onClick={props.onClearAll}
      size="lg"
      variant="outline"
    >
      <SlidersHorizontal aria-hidden="true" className="size-4" />
      {props.microcopies.clearAllFilters}
    </Button>
  );
}

export function ProductListingFiltersD2C(
  props: Readonly<ProductListingFiltersD2CProps>
) {
  const groupsWithOptions = props.filterGroups.filter(
    (group) => group.type === "range" || group.options.length > 0
  );
  const defaultOpenGroups = groupsWithOptions
    .filter((group) => group.defaultOpen || hasSelectedFilter(group))
    .map((group) => group.code);
  const selectedGroupCodes = groupsWithOptions
    .filter(hasSelectedFilter)
    .map((group) => group.code)
    .join("\u001f");
  const [openGroups, setOpenGroups] = useState(defaultOpenGroups);
  const showResetButton = props.showResetButton ?? true;

  useEffect(() => {
    if (!selectedGroupCodes) {
      return;
    }

    const selectedGroups = selectedGroupCodes.split("\u001f");

    setOpenGroups((currentGroups) => {
      const nextGroups = new Set(currentGroups);

      for (const group of selectedGroups) {
        nextGroups.add(group);
      }

      return nextGroups.size === currentGroups.length
        ? currentGroups
        : [...nextGroups];
    });
  }, [selectedGroupCodes]);

  return (
    <section
      aria-label={props.microcopies.filterProducts}
      className={cn(
        "bg-white px-3",
        showResetButton && "pb-5",
        props.className
      )}
      data-testid="product-listing-filter-panel"
    >
      {groupsWithOptions.length > 0 ? (
        <AccordionPrimitive
          onValueChange={setOpenGroups}
          type="multiple"
          value={openGroups}
        >
          {groupsWithOptions.map((group) => (
            <AccordionItem key={group.code} value={group.code} variant="d2c">
              <AccordionTrigger
                className="py-4 font-brand font-normal text-base leading-6 [&>svg]:size-6"
                variant="b2b"
              >
                {group.title}
              </AccordionTrigger>
              <AccordionContent className="pb-4 [&>div]:pb-0" variant="d2c">
                {group.type === "range" ? (
                  <ProductRangeFilterD2C
                    disabled={props.disabled}
                    group={group}
                    microcopies={props.microcopies}
                    onChange={props.onRangeChange}
                  />
                ) : (
                  <ProductOptionFilterD2C
                    disabled={props.disabled}
                    group={group}
                    onChange={props.onFilterChange}
                  />
                )}
              </AccordionContent>
            </AccordionItem>
          ))}
        </AccordionPrimitive>
      ) : (
        <div className="py-8">
          <TextD2C style="no-mb">
            {props.microcopies.noFilterOptionsAvailable}
          </TextD2C>
        </div>
      )}

      {showResetButton ? (
        <ProductListingResetFiltersButtonD2C
          activeFilters={props.activeFilters}
          className="mt-6"
          disabled={props.disabled}
          microcopies={props.microcopies}
          onClearAll={props.onClearAll}
        />
      ) : null}
    </section>
  );
}

export type ProductListingMobileFiltersD2CProps =
  ProductListingFiltersD2CProps & {
    onApply: () => void;
    onOpenChange: (open: boolean) => void;
    open: boolean;
  };

export function ProductListingMobileFiltersD2C(
  props: Readonly<ProductListingMobileFiltersD2CProps>
) {
  return (
    <Dialog onOpenChange={props.onOpenChange} open={props.open}>
      <DialogContent
        animation="slide-from-left"
        aria-describedby={undefined}
        className="flex flex-col lg:hidden"
        data-testid="product-listing-filter-mobile-menu"
        hideCloseButton
        overlayClassName="bg-black/50 lg:hidden"
        side="left"
      >
        <header className="flex shrink-0 items-center justify-between border-alabastergrey border-b px-3 pt-8 pb-4">
          <DialogTitle className="font-bold font-brand text-xl leading-6">
            {props.microcopies.filterTitle}
          </DialogTitle>
          <DialogClose asChild>
            <Button
              aria-label={props.microcopies.closeFilters}
              className="-mr-2"
              size="icon"
              variant="ghost"
            >
              <X aria-hidden="true" className="size-6" />
            </Button>
          </DialogClose>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <ProductListingFiltersD2C
            activeFilters={props.activeFilters}
            disabled={props.disabled}
            filterGroups={props.filterGroups}
            microcopies={props.microcopies}
            onClearAll={props.onClearAll}
            onFilterChange={props.onFilterChange}
            onRangeChange={props.onRangeChange}
            showResetButton={false}
          />
        </div>

        <footer className="shrink-0 space-y-3 px-3 pt-6 pb-[calc(3rem+env(safe-area-inset-bottom))]">
          <ProductListingResetFiltersButtonD2C
            activeFilters={props.activeFilters}
            disabled={props.disabled}
            microcopies={props.microcopies}
            onClearAll={props.onClearAll}
          />
          <Button
            className="w-full"
            disabled={props.disabled}
            onClick={props.onApply}
            size="lg"
          >
            {props.microcopies.applyFilters}
          </Button>
        </footer>
      </DialogContent>
    </Dialog>
  );
}
